import { html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import Styles from './vm-button-next.scss';

/**
 * A button component following the Vumedi Moderat design system.
 *
 * @slot - Default slot for the button label text.
 * @slot icon-leading - Slot for a leading icon (before the label).
 * @slot icon-trailing - Slot for a trailing icon (after the label).
 *
 * @csspart button - The inner button or anchor element.
 *
 * @example
 * ```html
 * <vm-button-next hierarchy="primary" size="md">Click me</vm-button-next>
 *
 * <vm-button-next hierarchy="secondary" size="sm">
 *   <vm-icon slot="icon-leading" name="arrow-left"></vm-icon>
 *   Back
 * </vm-button-next>
 *
 * <vm-button-next hierarchy="primary" icon-only aria-label="Search">
 *   <vm-icon slot="icon-leading" name="search"></vm-icon>
 * </vm-button-next>
 *
 * <vm-button-next href="/settings" hierarchy="link-color">Settings</vm-button-next>
 *
 * <vm-button-next hierarchy="primary-error">Delete</vm-button-next>
 * <vm-button-next hierarchy="tertiary-error">Remove</vm-button-next>
 * ```
 */
@customElement('vm-button-next')
export class VmButtonNext extends LitElement {
    static override styles = Styles;

    /**
     * Visual hierarchy of the button. The `*-error` variants recolor the matching shape
     * with the destructive (error) palette for irreversible / dangerous actions:
     * `primary-error` (solid red), `tertiary-error` (red outline), `quaternary-error`
     * (red ghost), `link-error` (red link).
     */
    @property({ reflect: true })
    hierarchy:
        | 'primary'
        | 'secondary'
        | 'tertiary'
        | 'quaternary'
        | 'primary-error'
        | 'tertiary-error'
        | 'quaternary-error'
        | 'link-color'
        | 'link-gray'
        | 'link-error' = 'primary';

    /** Size variant. */
    @property({ reflect: true })
    size: 'sm' | 'md' | 'lg' | 'xl' = 'md';

    /** Whether the button is disabled. */
    @property({ type: Boolean, reflect: true })
    disabled = false;

    /** Shows a loading spinner and prevents interaction. */
    @property({ type: Boolean, reflect: true })
    loading = false;

    /** Renders as a square icon-only button. Provide an aria-label for accessibility. */
    @property({ type: Boolean, reflect: true, attribute: 'icon-only' })
    iconOnly = false;

    /** Stretches the button to fill its container width. */
    @property({ type: Boolean, reflect: true, attribute: 'full-width' })
    fullWidth = false;

    /** Button type for form participation. */
    @property()
    type: 'button' | 'submit' | 'reset' = 'button';

    /** When set, renders as an anchor element instead of a button. */
    @property()
    href?: string;

    /** Target for anchor navigation. */
    @property()
    target?: string;

    /** Rel attribute for anchor. */
    @property()
    rel?: string;

    /** Download attribute for anchor. */
    @property()
    download?: string;

    /** Accessible label. Required for icon-only buttons. */
    @property({ attribute: 'aria-label' })
    override ariaLabel: string | null = null;

    /** Block clicks when disabled or loading (capture phase). */
    private _handleHostClick = (e: MouseEvent): void => {
        if (this.disabled || this.loading) {
            e.preventDefault();
            e.stopImmediatePropagation();
        }
    };

    /**
     * Proxy form submission/reset since a <button> inside Shadow DOM
     * cannot natively participate in ancestor form submission.
     */
    private _handleButtonClick = (): void => {
        if (this.type === 'button') return;

        const form = this.closest('form');
        if (!form) return;

        if (this.type === 'submit') {
            const proxy = document.createElement('button');
            proxy.type = 'submit';
            proxy.hidden = true;
            form.appendChild(proxy);
            proxy.click();
            proxy.remove();
        } else if (this.type === 'reset') {
            form.reset();
        }
    };

    override connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener('click', this._handleHostClick, { capture: true });
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener('click', this._handleHostClick, { capture: true });
    }

    private get _buttonContent() {
        return html`
            <slot name="icon-leading"></slot>
            <span class="label"><slot></slot></span>
            <slot name="icon-trailing"></slot>
        `;
    }

    private _renderButton() {
        return html`
            <button
                type=${this.type}
                ?disabled=${this.disabled}
                aria-label=${ifDefined(this.ariaLabel ?? undefined)}
                aria-busy=${ifDefined(this.loading ? 'true' : undefined)}
                @click=${this._handleButtonClick}
                part="button"
            >
                <span class="content">${this._buttonContent}</span>
                ${this.loading ? html`<span class="spinner" aria-hidden="true"></span>` : nothing}
            </button>
        `;
    }

    private _renderAnchor() {
        return html`
            <a
                href=${ifDefined(this.disabled ? undefined : this.href)}
                target=${ifDefined(this.target)}
                rel=${ifDefined(this.rel)}
                download=${ifDefined(this.download)}
                aria-label=${ifDefined(this.ariaLabel ?? undefined)}
                aria-disabled=${ifDefined(this.disabled ? 'true' : undefined)}
                aria-busy=${ifDefined(this.loading ? 'true' : undefined)}
                tabindex=${ifDefined(this.disabled ? '-1' : undefined)}
                part="button"
            >
                <span class="content">${this._buttonContent}</span>
                ${this.loading ? html`<span class="spinner" aria-hidden="true"></span>` : nothing}
            </a>
        `;
    }

    override render() {
        return this.href ? this._renderAnchor() : this._renderButton();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-button-next': VmButtonNext;
    }
}
